import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Plus, Check, X, Trash2, Moon, CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { fmtDate } from '../lib/dateUtils';

const TYPE_LABELS = { notturno: 'Notturno', sabato: 'Sabato', domenica: 'Domenica', festivo: 'Festivo', straordinario: 'Straordinario' };
const STATUS_MAP = {
  confermata: { l: 'Confermata', c: 'bg-green-50 text-green-700 border-green-200' },
  in_attesa: { l: 'In Attesa', c: 'bg-amber-50 text-amber-700 border-amber-200' },
  rifiutata: { l: 'Rifiutata', c: 'bg-red-50 text-red-700 border-red-200' }
};

export default function OnCallPage() {
  const { user, isRole } = useAuth();
  const canManage = isRole('admin', 'aspp', 'qhse', 'capo_cantiere');
  const [items, setItems] = useState([]);
  const [pending, setPending] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [calendarData, setCalendarData] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [calMonth, setCalMonth] = useState(new Date().getMonth() + 1);
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [form, setForm] = useState({ employee_id: '', date: '', oncall_type: 'notturno', notes: '' });

  const load = async () => {
    try {
      const [itemsRes, pendingRes, calRes] = await Promise.all([
        api.get('/oncall'),
        api.get('/oncall/my-pending'),
        api.get('/oncall/calendar', { params: { month: calMonth, year: calYear } })
      ]);
      setItems(itemsRes.data);
      setPending(pendingRes.data);
      setCalendarData(calRes.data);
      if (canManage) {
        try {
          const empRes = await api.get('/employees');
          setEmployees(empRes.data.filter(e => e.is_active));
        } catch {}
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [calMonth, calYear]);

  const handleCreate = async () => {
    try {
      const payload = { ...form };
      if (!canManage || !form.employee_id) {
        payload.employee_id = user?.employee_id || '';
      }
      await api.post('/oncall', payload);
      setShowCreate(false);
      setForm({ employee_id: '', date: '', oncall_type: 'notturno', notes: '' });
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleRespond = async (entryId, accepted) => {
    try {
      const notes = !accepted ? prompt('Motivo del rifiuto (opzionale):') || '' : '';
      await api.put(`/oncall/${entryId}/respond`, { accepted, notes });
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare?')) return;
    try { await api.delete(`/oncall/${id}`); load(); } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const changeMonth = (delta) => {
    let m = calMonth + delta;
    let y = calYear;
    if (m > 12) { m = 1; y++; }
    if (m < 1) { m = 12; y--; }
    setCalMonth(m);
    setCalYear(y);
  };

  // Calendar grid generation
  const getDaysInMonth = (month, year) => new Date(year, month, 0).getDate();
  const getFirstDayOfMonth = (month, year) => {
    const d = new Date(year, month - 1, 1).getDay();
    return d === 0 ? 6 : d - 1; // Monday=0
  };

  const monthNames = ['', 'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

  const totalDays = getDaysInMonth(calMonth, calYear);
  const firstDay = getFirstDayOfMonth(calMonth, calYear);

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-pulse text-slate-400">Caricamento...</div></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="oncall-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2"><Moon size={24} /> Reperibilita</h1>
        <Button onClick={() => setShowCreate(true)} data-testid="add-oncall-btn"><Plus size={16} className="mr-1" />
          {canManage ? 'Assegna Reperibilita' : 'Dichiara Disponibilita'}
        </Button>
      </div>

      {/* Pending responses for employees */}
      {pending.length > 0 && (
        <Card className="border-amber-300 bg-amber-50 shadow-sm" data-testid="pending-oncall">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-amber-800">Reperibilita da confermare</CardTitle></CardHeader>
          <CardContent>
            {pending.map(p => (
              <div key={p.id} className="flex items-center justify-between py-2 border-b border-amber-200 last:border-0">
                <div>
                  <span className="font-medium text-sm">{fmtDate(p.date)}</span>
                  <Badge variant="outline" className="ml-2">{TYPE_LABELS[p.oncall_type]}</Badge>
                  {p.notes && <span className="text-xs text-slate-500 ml-2">{p.notes}</span>}
                  {p.assigned_by_name && <span className="text-xs text-slate-400 ml-2">da {p.assigned_by_name}</span>}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => handleRespond(p.id, true)} data-testid={`accept-${p.id}`}><Check size={14} className="mr-1" /> Si</Button>
                  <Button size="sm" variant="destructive" onClick={() => handleRespond(p.id, false)} data-testid={`reject-${p.id}`}><X size={14} className="mr-1" /> No</Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="calendario">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="calendario">Calendario</TabsTrigger>
          <TabsTrigger value="elenco">Elenco</TabsTrigger>
        </TabsList>

        <TabsContent value="calendario">
          <Card className="border-slate-200 shadow-sm mt-4">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <Button variant="ghost" size="icon" onClick={() => changeMonth(-1)}><ChevronLeft size={18} /></Button>
                <CardTitle className="text-base">{monthNames[calMonth]} {calYear}</CardTitle>
                <Button variant="ghost" size="icon" onClick={() => changeMonth(1)}><ChevronRight size={18} /></Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium text-slate-500 mb-2">
                {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map(d => <div key={d}>{d}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: firstDay }).map((_, i) => <div key={`empty-${i}`} />)}
                {Array.from({ length: totalDays }).map((_, i) => {
                  const day = i + 1;
                  const dateStr = `${calYear}-${String(calMonth).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                  const dayEntries = calendarData[dateStr] || [];
                  const dayOfWeek = new Date(calYear, calMonth - 1, day).getDay();
                  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                  return (
                    <div key={day} className={`min-h-[60px] border rounded p-1 text-xs ${isWeekend ? 'bg-slate-50' : ''} ${dayEntries.length > 0 ? 'border-blue-300 bg-blue-50' : 'border-slate-200'}`}>
                      <div className={`font-medium mb-0.5 ${isWeekend ? 'text-red-500' : ''}`}>{day}</div>
                      {dayEntries.map((e, idx) => (
                        <div key={idx} className={`text-[10px] leading-tight truncate rounded px-0.5 mb-0.5 ${e.status === 'confermata' ? 'bg-green-200 text-green-800' : 'bg-amber-200 text-amber-800'}`}>
                          {e.employee_name?.split(' ')[0]} - {TYPE_LABELS[e.oncall_type]?.[0]}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="elenco">
          <Card className="border-slate-200 shadow-sm mt-4">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Data</TableHead><TableHead>Dipendente</TableHead><TableHead>Tipo</TableHead>
                  <TableHead>Stato</TableHead><TableHead>Note</TableHead><TableHead>Azioni</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-500">Nessuna reperibilita</TableCell></TableRow>
                  ) : items.map(item => {
                    const s = STATUS_MAP[item.status] || STATUS_MAP.in_attesa;
                    return (
                      <TableRow key={item.id} className="hover:bg-slate-50">
                        <TableCell className="text-sm font-medium">{fmtDate(item.date)}</TableCell>
                        <TableCell className="text-sm">{item.employee_name}</TableCell>
                        <TableCell><Badge variant="outline">{TYPE_LABELS[item.oncall_type]}</Badge></TableCell>
                        <TableCell><Badge variant="outline" className={s.c}>{s.l}</Badge></TableCell>
                        <TableCell className="text-sm text-slate-500 max-w-[200px] truncate">{item.notes || '-'}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {item.status === 'in_attesa' && item.employee_id === user?.employee_id && (
                              <>
                                <Button size="sm" variant="ghost" onClick={() => handleRespond(item.id, true)} className="text-green-600"><Check size={14} /></Button>
                                <Button size="sm" variant="ghost" onClick={() => handleRespond(item.id, false)} className="text-red-600"><X size={14} /></Button>
                              </>
                            )}
                            {(canManage || item.employee_id === user?.employee_id) && (
                              <Button size="sm" variant="ghost" onClick={() => handleDelete(item.id)} className="text-red-600"><Trash2 size={14} /></Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{canManage ? 'Assegna Reperibilita' : 'Dichiara Disponibilita'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {canManage && (
              <div className="space-y-1.5">
                <Label>Dipendente *</Label>
                <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                  <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name}</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-xs text-slate-500">Il dipendente dovra confermare SI/NO</p>
              </div>
            )}
            {!canManage && <p className="text-sm bg-green-50 border border-green-200 rounded p-3 text-green-700">Stai dichiarando la tua disponibilita per reperibilita. Sara confermata automaticamente.</p>}
            <div className="space-y-1.5"><Label>Data *</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            <div className="space-y-1.5">
              <Label>Tipo *</Label>
              <Select value={form.oncall_type} onValueChange={(v) => setForm({ ...form, oncall_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="notturno">Notturno</SelectItem>
                  <SelectItem value="sabato">Sabato</SelectItem>
                  <SelectItem value="domenica">Domenica</SelectItem>
                  <SelectItem value="festivo">Festivo</SelectItem>
                  <SelectItem value="straordinario">Straordinario</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Note</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Opzionale..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Annulla</Button>
            <Button onClick={handleCreate} disabled={!form.date}>Conferma</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
